import { motion } from "framer-motion";
import { FC, ReactNode, useEffect, useState } from "react";
import "./VisualStory.scss";
import { faClose, faPause, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "./Modal";
import DefaultThumbNail from "../images/blog-article/default.png";

interface IProps {
  slides: { image: string; content: ReactNode }[];
  isOpen: boolean;
  handleClose: () => void;
}

const DELAY = 5000;

const VisualStory: FC<IProps> = ({ slides, isOpen, handleClose }) => {
  const slideCount = slides.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [pausedTime, setPausedTime] = useState<number>();

  // reset visual story
  useEffect(() => {
    if (!isOpen) return;

    setCurrentIndex(0);
    setPaused(false);
    setStartTime(new Date());
    setPausedTime(undefined);
  }, [isOpen]);

  useEffect(() => {
    if (paused) {
      setPausedTime(
        (prev) => new Date().getTime() - startTime.getTime() + (prev || 0)
      );
      setStartTime(new Date());
      return;
    }

    setStartTime(new Date());

    let delay = DELAY;

    if (pausedTime) delay = DELAY - (pausedTime || 0);

    const timer = setTimeout(() => {
      if (currentIndex + 1 < slideCount) goToNextSlide();
      else handleClose();
      setPausedTime(undefined);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [isOpen, currentIndex, paused]);

  const goToNextSlide = () => {
    setCurrentIndex((prev) => prev + 1);
    setPaused(false);
  };
  const goToPrevSlide = () => {
    setCurrentIndex((prev) => prev - 1);
    setPaused(false);
  };
  const togglePause = () => setPaused((prev) => !prev);

  if (!(slides && slides.length > 0) || !isOpen) return;

  return (
    <>
      <div
        className="fixed -top-8 -bottom-8 -left-8 -right-8 z-30 transition-all duration-500"
        style={{
          background: "white",
          backgroundImage: `url('${slides[currentIndex].image}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(24px)",
        }}
        onClick={handleClose}
      ></div>
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center h-screen lg:h-4/5 gap-4 w-fit z-40">
        <button
          disabled={currentIndex == 0}
          onClick={goToPrevSlide}
          className="hidden lg:block disabled:opacity-50"
        >
          {prevBtnIcon}
        </button>
        <div className="relative h-full overflow-hidden max-sm:w-screen w-max max-w-[100vw] lg:rounded-3xl">
          <div className="absolute top-0 left-0 flex w-full gap-2 p-2">
            {Array.from({ length: slideCount }, (_, index) => (
              <div className="flex-1 bg-grey-3">
                <div
                  className={`progress-bar ${
                    index < currentIndex
                      ? "completed"
                      : index > currentIndex
                      ? ""
                      : paused
                      ? "current paused"
                      : "current"
                  }`}
                ></div>
              </div>
            ))}
          </div>
          <img
            className="object-cover h-full w-full"
            src={slides[currentIndex].image || DefaultThumbNail}
          />
          <div className="absolute flex gap-4 top-6 right-6">
            <button onClick={togglePause}>
              <FontAwesomeIcon
                icon={paused ? faPlay : faPause}
                size="lg"
                className="text-white"
              />
            </button>
            <button onClick={handleClose}>
              <FontAwesomeIcon
                icon={faClose}
                size="lg"
                className="text-white"
              />
            </button>
          </div>
          <motion.div
            key={currentIndex}
            initial={{ y: "100%", x: "-50%" }}
            animate={{ y: "0%", x: "-50%" }}
            exit={{ y: "0%", x: "-50%" }}
            transition={{ duration: 1, ease: [0, 0.88, 0, 1] }}
            className={`absolute w-[110%] bottom-0 left-1/2 bg-[#00000077] py-12 px-12`}
            style={{
              borderTopLeftRadius: "50% 75px",
              borderTopRightRadius: "50% 75px",
            }}
          >
            {slides[currentIndex].content}
          </motion.div>
          <button
            disabled={currentIndex == 0}
            onClick={goToPrevSlide}
            className="absolute -translate-y-1/2 top-1/2 left-4 lg:hidden disabled:opacity-50"
          >
            {prevBtnIcon}
          </button>
          <button
            disabled={currentIndex + 1 == slideCount}
            onClick={goToNextSlide}
            className="absolute -translate-y-1/2 top-1/2 right-4 lg:hidden disabled:opacity-50"
          >
            {nextBtnIcon}
          </button>
        </div>
        <button
          disabled={currentIndex + 1 == slideCount}
          onClick={goToNextSlide}
          className="hidden lg:block disabled:opacity-50"
        >
          {nextBtnIcon}
        </button>
      </div>
    </>
  );
};

export default VisualStory;

const prevBtnIcon = (
  <div className="flex justify-center items-center p-2.5 w-11 h-11 bg-white border border-solid border-black rounded-[50px]">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M19 11H9L12.29 7.71C12.3837 7.61703 12.4581 7.50643 12.5089 7.38457C12.5597 7.26272 12.5858 7.13201 12.5858 7C12.5858 6.86799 12.5597 6.73728 12.5089 6.61542C12.4581 6.49356 12.3837 6.38296 12.29 6.29C12.1026 6.10375 11.8492 5.99921 11.585 5.99921C11.3208 5.99921 11.0674 6.10375 10.88 6.29L6.59 10.59C6.21441 10.9633 6.00223 11.4704 6 12C6.00487 12.5261 6.21684 13.0291 6.59 13.4L10.88 17.7C10.9732 17.7926 11.0838 17.8659 11.2054 17.9157C11.3269 17.9656 11.4571 17.991 11.5885 17.9905C11.7199 17.9901 11.8499 17.9637 11.9712 17.913C12.0924 17.8623 12.2024 17.7882 12.295 17.695C12.3876 17.6017 12.4609 17.4912 12.5107 17.3696C12.5606 17.248 12.586 17.1178 12.5856 16.9864C12.5851 16.8551 12.5588 16.725 12.508 16.6038C12.4573 16.4826 12.3832 16.3726 12.29 16.28L9 13H19C19.2652 13 19.5196 12.8946 19.7071 12.7071C19.8946 12.5196 20 12.2652 20 12C20 11.7348 19.8946 11.4804 19.7071 11.2929C19.5196 11.1054 19.2652 11 19 11Z"
        fill="#333333"
      />
    </svg>
  </div>
);

const nextBtnIcon = (
  <div className=" flex justify-center items-center p-2.5 w-11 h-11 bg-white border border-solid border-black rounded-[50px]">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M5 11H15L11.71 7.71C11.6163 7.61703 11.5419 7.50643 11.4911 7.38457C11.4403 7.26272 11.4142 7.13201 11.4142 7C11.4142 6.86799 11.4403 6.73728 11.4911 6.61542C11.5419 6.49356 11.6163 6.38296 11.71 6.29C11.8974 6.10375 12.1508 5.99921 12.415 5.99921C12.6792 5.99921 12.9326 6.10375 13.12 6.29L17.41 10.59C17.7856 10.9633 17.9978 11.4704 18 12C17.9951 12.5261 17.7832 13.0291 17.41 13.4L13.12 17.7C13.0268 17.7926 12.9162 17.8659 12.7946 17.9157C12.6731 17.9656 12.5429 17.991 12.4115 17.9905C12.2801 17.9901 12.1501 17.9637 12.0288 17.913C11.9076 17.8623 11.7976 17.7882 11.705 17.695C11.6124 17.6017 11.5391 17.4912 11.4893 17.3696C11.4394 17.248 11.414 17.1178 11.4144 16.9864C11.4149 16.8551 11.4412 16.725 11.492 16.6038C11.5427 16.4826 11.6168 16.3726 11.71 16.28L15 13H5C4.73478 13 4.48043 12.8946 4.29289 12.7071C4.10536 12.5196 4 12.2652 4 12C4 11.7348 4.10536 11.4804 4.29289 11.2929C4.48043 11.1054 4.73478 11 5 11Z"
        fill="#333333"
      />
    </svg>
  </div>
);
